import {
  formatPartitionBudgetCurrency,
  getPartitionBudgetAccessibilityLabel,
  getPartitionBudgetEstimate,
  getPartitionBudgetInstallationLabel,
  getPartitionBudgetMaterial,
  getPartitionBudgetPrivacyLabel,
  isPartitionBudgetInput,
  getPartitionBudgetQuoteMaterialPreference as getMaterialPreference,
  type PartitionBudgetInput,
} from "@/data/partitionBudgetEstimates";

export const PARTITION_BUDGET_QUOTE_HANDOFF_STORAGE_KEY = "partition-stalls-partition-budget-quote-handoff-v2";

export type PartitionBudgetQuoteHandoff = {
  configuration: PartitionBudgetInput;
  lowCents: number;
  highCents: number;
  quantity: string;
  message: string;
};

/**
 * Browser storage is not trusted. The handoff always recalculates from the
 * approved owner-provided planning ranges before anything is copied into the
 * secure quote request.
 */
export function buildPartitionBudgetQuoteHandoff(value: unknown): PartitionBudgetQuoteHandoff | null {
  if (!isPartitionBudgetInput(value)) return null;

  const estimate = getPartitionBudgetEstimate(value);
  const material = getPartitionBudgetMaterial(value.materialId);
  if (!estimate || !material) return null;

  const stallLabel = `${value.stalls} commercial toilet ${value.stalls === 1 ? "compartment" : "compartments"}`;
  const message = [
    "Partition budget calculator — planning range, not a quote or order total.",
    "",
    "Selected planning direction:",
    `• Material: ${material.name}`,
    `• Base material planning range: ${formatPartitionBudgetCurrency(estimate.basePerCompartment.lowCents)}–${formatPartitionBudgetCurrency(estimate.basePerCompartment.highCents)} per compartment`,
    `• Extra privacy: ${getPartitionBudgetPrivacyLabel(value.privacy)}`,
    `• Installation: ${getPartitionBudgetInstallationLabel(value.installation)}`,
    `• Selected additions: ${formatPartitionBudgetCurrency(estimate.selectedAdditionsPerStallCents)} per compartment (${estimate.selectedAdditionsLabel})`,
    `• Accessibility: ${getPartitionBudgetAccessibilityLabel(value.accessibility)}`,
    `• Quantity: ${stallLabel}`,
    "",
    `${estimate.basisLabel} shown by calculator: ${formatPartitionBudgetCurrency(estimate.lowCents)}–${formatPartitionBudgetCurrency(estimate.highCents)}.`,
    `Per-compartment planning allowance with selected additions: ${formatPartitionBudgetCurrency(estimate.perCompartment.lowCents)}–${formatPartitionBudgetCurrency(estimate.perCompartment.highCents)}.`,
    "",
    "Please confirm dimensions, manufacturer/series, mounting style, screens and accessories, freight, tax, availability, installation scope, and final project price.",
  ].join("\n");

  return {
    configuration: value,
    lowCents: estimate.lowCents,
    highCents: estimate.highCents,
    quantity: `${value.stalls} stall${value.stalls === 1 ? "" : "s"} — budget calculator planning range`,
    message,
  };
}

export function savePartitionBudgetQuoteHandoff(handoff: PartitionBudgetQuoteHandoff): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(
    PARTITION_BUDGET_QUOTE_HANDOFF_STORAGE_KEY,
    JSON.stringify({ configuration: handoff.configuration }),
  );
}

export function consumePartitionBudgetQuoteHandoff(): PartitionBudgetQuoteHandoff | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PARTITION_BUDGET_QUOTE_HANDOFF_STORAGE_KEY);
    window.sessionStorage.removeItem(PARTITION_BUDGET_QUOTE_HANDOFF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { configuration?: unknown };
    return buildPartitionBudgetQuoteHandoff(parsed.configuration);
  } catch {
    window.sessionStorage.removeItem(PARTITION_BUDGET_QUOTE_HANDOFF_STORAGE_KEY);
    return null;
  }
}

export function getPartitionBudgetQuoteMaterialPreference(input: PartitionBudgetInput) {
  return getMaterialPreference(input.materialId);
}

export function getPartitionBudgetQuoteBasisLabel(input: PartitionBudgetInput) {
  const estimate = getPartitionBudgetEstimate(input);
  return estimate?.basisLabel ?? "Material planning range";
}
